/**
 * A best-before date, in the shop's own format — the room's lot card and the host panel's lot row both.
 *
 * ## The format is the storefront's, deliberately
 *
 * `peeko.co.uk` prints `31/08/26` — `DD/MM/YY`, in monospace, between two hairlines — on every
 * product card, and a customer arriving from a show link has very likely just seen it there. The
 * room repeating it in a different shape would read as a different fact about the product.
 *
 * ## Why this is string work and not date work
 *
 * `R-LOT-30`: *`best_before` is a plain date and is **not** timezone-converted.* §18.6 puts every
 * instant in this platform in UTC and renders it in the customer's zone, which is right for *when
 * something happened* and wrong for what is printed on a pack — that is the same day in Falmouth
 * and in Frankfurt.
 *
 * The obvious spelling gets this wrong in a way that is easy to ship and hard to see:
 *
 * ```
 *   new Date("2027-03-31").toLocaleDateString("en-GB")
 *     → "31/03/2027" in the UK
 *     → "30/03/2027" anywhere west of Greenwich
 * ```
 *
 * A bare `YYYY-MM-DD` parses as **UTC midnight**, and the formatter then renders it in the
 * viewer's zone. For a food product that is the difference between in date and out of it.
 *
 * So the value is never made into a `Date` at all. Rearranging three groups of digits cannot move
 * a day no matter where it is read, which makes the requirement structurally true here rather than
 * something a future edit has to remember.
 */
export function formatBestBefore(value: string): string {
  // Anything that is not a plain date is shown as it came rather than mangled into one. The API
  // only ever sends this shape (`shopify/variants.ts` validates it), so this is the belt.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

  //  YYYY - MM - DD  →  DD/MM/YY
  //  0123   56   89         the test above is what makes these offsets safe
  return `${value.slice(8, 10)}/${value.slice(5, 7)}/${value.slice(2, 4)}`;
}
